import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { validateName, validateEmail, validatePhone, validateCpfCnpj, validateForm } from "@/utils/validators";
import { errorHandler } from "@/utils/errorHandler";
import { logger } from "@/utils/logger";
import { performanceMonitor } from "@/utils/performanceMonitor";
import { supabase } from "@/integrations/supabase/client";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CreditCard, 
  Settings, 
  LogOut,
  Edit,
  Save,
  X,
  Flag,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { Country, COUNTRIES, AVAILABLE_COUNTRIES } from "@/types/internationalization";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { uploadCompanyLogo } from "@/integrations/supabase/storage";

export default function MinhaConta() {
  const navigate = useNavigate();
  const { empresa, user, logout, refreshEmpresa } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { refreshCountry } = useInternationalization();
  const [isEditing, setIsEditing] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);
  const [dialogCancelarContaOpen, setDialogCancelarContaOpen] = useState(false);
  const [confirmacaoCancelamento, setConfirmacaoCancelamento] = useState("");
  const [cancelandoConta, setCancelandoConta] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoRemoved, setLogoRemoved] = useState(false);

  // Buscar email do usuário (primeiro tenta profiles, depois empresas)
  const { data: userProfile } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      try {
        // Primeiro tenta buscar na tabela profiles
        const { data, error } = await supabase
          .from("profiles")
          .select("email")
          .eq("user_id", user.id)
          .single();
        
        if (error && error.code === 'PGRST116') {
          // Perfil não existe, usar email da empresa
          console.log("Perfil não encontrado, usando email da empresa");
          return { email: empresa?.email || user.email || "" };
        }
        
        if (error) {
          console.warn("Erro ao buscar perfil do usuário:", error);
          return { email: empresa?.email || user.email || "" };
        }
        
        return data;
      } catch (error) {
        console.warn("Erro geral ao buscar perfil:", error);
        return { email: empresa?.email || user.email || "" };
      }
    },
    enabled: !!user?.id,
  });

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    endereco: "",
    responsavel: "",
    cpf_cnpj: "",
    country: "BR",
  });

  // Atualizar formData quando empresa e userProfile carregarem
  useEffect(() => {
    if (empresa) {
      setFormData(prev => ({
        ...prev,
        nome: empresa.nome || "",
        telefone: empresa.telefone || "",
        endereco: empresa.endereco || "",
        responsavel: empresa.responsavel || "",
        cpf_cnpj: empresa.cpf_cnpj || "",
        country: empresa.country || "BR",
      }));
      setCurrentLogoUrl(empresa.logo_url || null);
      setLogoPreview(null);
      setSelectedLogo(null);
      setLogoRemoved(false);
    }
  }, [empresa]);

  // Atualizar email quando userProfile carregar
  useEffect(() => {
    if (userProfile?.email) {
      setFormData(prev => ({
        ...prev,
        email: userProfile.email
      }));
    }
  }, [userProfile?.email]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'country' && value !== empresa?.country) {
      setShouldReload(true);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("A logo deve ter no máximo 5MB");
      return;
    }

    setSelectedLogo(file);
    setLogoRemoved(false);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setSelectedLogo(null);
    setLogoPreview(null);
    setCurrentLogoUrl(null);
    setLogoRemoved(true);
  };


  // Atualizar contexto quando o país for alterado
  useEffect(() => {
    if (shouldReload) {
      toast.info("País alterado! Aplicando mudanças...", { duration: 1500 });
      const timer = setTimeout(() => {
        // Invalidar cache da empresa para forçar reload
        invalidateRelated('empresas');
        queryClient.refetchQueries({ queryKey: ['empresa'] });
        
        // Atualizar contexto de internacionalização
        refreshCountry();
        
        setShouldReload(false);
        toast.success("Mudanças aplicadas com sucesso!");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [shouldReload, refreshCountry, invalidateRelated, queryClient]);

  const handleSave = async () => {
    // Validação robusta
    const validation = validateForm(
      {
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone, 
        cpf_cnpj: formData.cpf_cnpj,
        responsavel: formData.responsavel,
        endereco: formData.endereco
      },
      {
        nome: validateName,
        email: validateEmail,
        telefone: validatePhone,
        cpf_cnpj: validateCpfCnpj,
        responsavel: (value) => value ? validateName(value) : { isValid: true, errors: [] },
        endereco: (value) => value ? { isValid: true, errors: [] } : { isValid: true, errors: [] }
      }
    );
    
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      // Medir performance da atualização
      await performanceMonitor.measure(
        'updateCompanyData',
        async () => {
          // Importar supabase
          const { supabase } = await import("@/integrations/supabase/client");
          
          const payloadUpdate = {
            nome: formData.nome,
            telefone: formData.telefone,
            responsavel: formData.responsavel,
            endereco: formData.endereco,
            cpf_cnpj: formData.cpf_cnpj,
            country: formData.country,
            updated_at: new Date().toISOString()
          };

          let logoUrl = currentLogoUrl;

          if (selectedLogo && empresa?.id) {
            setIsUploadingLogo(true);
            const uploadResult = await uploadCompanyLogo(selectedLogo, empresa.id);
            setIsUploadingLogo(false);

            if (!uploadResult.ok) {
              toast.error(uploadResult.error || "Erro ao enviar a logo");
              throw new Error(uploadResult.error || "Erro ao enviar a logo");
            }

            logoUrl = uploadResult.url || null;
          } else if (logoRemoved) {
            logoUrl = null;
          }

          if (logoUrl !== undefined) {
            (payloadUpdate as typeof payloadUpdate & { logo_url?: string | null }).logo_url = logoUrl;
          }
          
          // Atualizar dados da empresa no Supabase
          const { error } = await supabase
            .from("empresas")
            .update(payloadUpdate)
            .eq("id", empresa?.id);

          if (error) {
            throw error;
          }

          // Atualizar email no perfil do usuário (se a tabela existir)
          if (formData.email && user?.id) {
            try {
              const { error: profileError } = await supabase
                .from("profiles")
                .update({
                  email: formData.email,
                  updated_at: new Date().toISOString()
                })
                .eq("user_id", user.id);

              if (profileError) {
                console.warn("Erro ao atualizar email no perfil:", profileError);
                // Se a tabela não existir, atualizar na empresa
                const { error: empresaEmailError } = await supabase
                  .from("empresas")
                  .update({ email: formData.email })
                  .eq("id", empresa?.id);
                
                if (empresaEmailError) {
                  console.warn("Erro ao atualizar email na empresa:", empresaEmailError);
                }
              }
            } catch (error) {
              console.warn("Erro ao tentar atualizar perfil:", error);
              // Fallback: atualizar email na empresa
              const { error: empresaEmailError } = await supabase
                .from("empresas")
                .update({ email: formData.email })
                .eq("id", empresa?.id);
              
              if (empresaEmailError) {
                console.warn("Erro ao atualizar email na empresa:", empresaEmailError);
              }
            }
          }

          if (error) {
            throw error;
          }

          return { success: true };
        },
        'MinhaConta'
      );

      logger.userAction('company_data_updated', 'MINHA_CONTA', { 
        companyId: empresa?.id, 
        fields: Object.keys(formData).filter(key => formData[key as keyof typeof formData])
      });
      
      toast.success("Dados atualizados com sucesso!");
      setIsEditing(false);
      await refreshEmpresa(true);

      // Invalidar cache e recursos relacionados
      invalidateRelated('empresas');
    } catch (error: unknown) {
      const appError = errorHandler.handleSupabaseError(error, 'updateCompanyData');
      logger.error('Falha ao atualizar dados da empresa', 'MINHA_CONTA', { 
        companyId: empresa?.id, 
        error: error.message 
      });
      toast.error(appError.message);
    }
  };

  const handleCancel = () => {
    setFormData({
      nome: empresa?.nome || "",
      email: empresa?.email || "",
      telefone: empresa?.telefone || "",
      endereco: empresa?.endereco || "",
      responsavel: empresa?.responsavel || "",
      cpf_cnpj: empresa?.cpf_cnpj || "",
      country: empresa?.country || "BR",
    });
    setSelectedLogo(null);
    setLogoPreview(null);
    setCurrentLogoUrl(empresa?.logo_url || null);
    setLogoRemoved(false);
    setIsEditing(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
      toast.success("Logout realizado com sucesso!");
    } catch (error) {
      toast.error("Erro ao fazer logout");
    }
  };

  const handleCancelarConta = async () => {
    if (confirmacaoCancelamento.toLowerCase() !== "cancelar") {
      toast.error('Digite "cancelar" para confirmar');
      return;
    }

    setCancelandoConta(true);

    try {
      // 1. Cancelar assinatura no ASAAS (se existir)
      try {
        const empresaId = empresa?.id;
        if (empresaId) {
          // Buscar assinatura ativa
          const { data: payments } = await supabase
            .from('payments')
            .select('*')
            .eq('empresa_id', empresaId)
            .eq('status', 'active')
            .limit(1);

          if (payments && payments.length > 0) {
            // Tentar cancelar no ASAAS via API
            const response = await fetch('/api/asaas', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'cancelSubscription',
                data: { subscriptionId: payments[0].asaas_subscription_id }
              })
            });

            if (!response.ok) {
              console.warn('Erro ao cancelar assinatura no ASAAS, continuando...');
            }
          }
        }
      } catch (error) {
        console.warn('Erro ao cancelar assinatura:', error);
        // Continuar mesmo se falhar
      }

      // 2. Deletar dados do usuário (ou marcar como deletado)
      // Por segurança, vamos apenas desativar a empresa
      if (empresa?.id) {
        await supabase
          .from('empresas')
          .update({ status: 'deleted', updated_at: new Date().toISOString() })
          .eq('id', empresa.id);
      }

      // 3. Marcar dados do usuário como deletado (não deletar completamente por segurança)
      // Por segurança, não deletamos a conta do Auth automaticamente
      // O usuário pode fazer isso manualmente ou via suporte
      
      // Fazer logout após marcar como deletado
      await logout();

      toast.success("Conta cancelada com sucesso!");
      navigate("/login");
    } catch (error: any) {
      console.error('Erro ao cancelar conta:', error);
      toast.error(error?.message || "Erro ao cancelar conta. Entre em contato com o suporte.");
    } finally {
      setCancelandoConta(false);
      setDialogCancelarContaOpen(false);
      setConfirmacaoCancelamento("");
    }
  };

  const getTrialStatus = () => {
    // PRIMEIRO: Verificar se o usuário tem premium ativo
    if (empresa?.is_premium === true) {
      return { status: "premium", days: 0 };
    }
    
    if (!empresa?.trial_end_date) return { status: "unknown", days: 0 };
    
    const trialEnd = new Date(empresa.trial_end_date);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return { status: "expired", days: 0 };
    } else if (diffDays <= 3) {
      return { status: "expiring", days: diffDays };
    } else {
      return { status: "active", days: diffDays };
    }
  };

  const trialStatus = getTrialStatus();

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center gap-4 p-6 border-b border-border">
        <SidebarTrigger />
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Minha Conta</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie suas informações e configurações
          </p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Status da Assinatura */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Status da Assinatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {trialStatus.status === "premium" ? "Assinatura Premium" : "Período de Teste"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {trialStatus.status === "premium" 
                    ? "Sua assinatura está ativa e você tem acesso a todos os recursos premium"
                    : trialStatus.status === "expired" 
                    ? "Período de teste expirado" 
                    : trialStatus.status === "expiring"
                    ? `${trialStatus.days} dias restantes`
                    : `${trialStatus.days} dias restantes`
                  }
                </p>
              </div>
              <Badge 
                variant={trialStatus.status === "premium" ? "default" :
                        trialStatus.status === "expired" ? "destructive" : 
                        trialStatus.status === "expiring" ? "secondary" : "default"}
                className={trialStatus.status === "premium" ? "bg-green-100 text-green-800 border-green-200" : ""}
              >
                {trialStatus.status === "premium" ? "Ativo" :
                 trialStatus.status === "expired" ? "Expirado" :
                 trialStatus.status === "expiring" ? "Expirando" : "Ativo"}
              </Badge>
            </div>
            {trialStatus.status === "expired" && (
              <div className="mt-4">
                <Button 
                  onClick={() => navigate("/assinatura")}
                  className="w-full"
                >
                  Renovar Assinatura
                </Button>
              </div>
            )}
            {trialStatus.status === "premium" && (
              <div className="mt-4">
                <Button 
                  onClick={() => navigate("/assinatura")}
                  variant="outline"
                  className="w-full"
                >
                  Gerenciar Assinatura
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Informações da Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Informações da Empresa
              </div>
              {!isEditing ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCancel}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleSave}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">
                  Nome da Empresa <span className="text-red-500">*</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleInputChange("nome", e.target.value)}
                    placeholder="Nome da sua empresa"
                  />
                ) : (
                  <p className="text-sm font-medium">{formData.nome || "Não informado"}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="seu@email.com"
                  />
                ) : (
                  <p className="text-sm font-medium">{formData.email || "Não informado"}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">
                  Telefone <span className="text-red-500">*</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => handleInputChange("telefone", e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                ) : (
                  <p className="text-sm font-medium">{formData.telefone || "Não informado"}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsavel">
                  Responsável <span className="text-red-500">*</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="responsavel"
                    value={formData.responsavel}
                    onChange={(e) => handleInputChange("responsavel", e.target.value)}
                    placeholder="Nome do responsável"
                  />
                ) : (
                  <p className="text-sm font-medium">{formData.responsavel || "Não informado"}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf_cnpj">
                  CPF/CNPJ <span className="text-red-500">*</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="cpf_cnpj"
                    value={formData.cpf_cnpj}
                    onChange={(e) => handleInputChange("cpf_cnpj", e.target.value)}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                ) : (
                  <p className="text-sm font-medium">{formData.cpf_cnpj || "Não informado"}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">
                  País <span className="text-red-500">*</span>
                  {shouldReload && (
                    <span className="ml-2 text-sm text-blue-600 font-medium">
                      (Aplicando mudanças...)
                    </span>
                  )}
                </Label>
                {isEditing ? (
                  <Select value={formData.country} onValueChange={(value) => handleInputChange("country", value)}>
                    <SelectTrigger className={shouldReload ? "border-blue-500 bg-blue-50" : ""}>
                      <SelectValue placeholder="Selecione seu país" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_COUNTRIES.map((country) => {
                        const config = COUNTRIES[country];
                        return (
                          <SelectItem key={country} value={country}>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{config.flag}</span>
                              <span>{config.name}</span>
                              <span className="text-muted-foreground text-sm">
                                ({config.currencySymbol})
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{COUNTRIES[formData.country as Country]?.flag}</span>
                    <p className="text-sm font-medium">{COUNTRIES[formData.country as Country]?.name}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="endereco">
                Endereço <span className="text-gray-400">(opcional)</span>
              </Label>
              {isEditing ? (
                <Input
                  id="endereco"
                  value={formData.endereco}
                  onChange={(e) => handleInputChange("endereco", e.target.value)}
                  placeholder="Endereço completo da empresa"
                />
              ) : (
                <p className="text-sm font-medium">{formData.endereco || "Não informado"}</p>
              )}
            </div>

            <div className="space-y-3">
              <Label>Logo da Empresa</Label>
              <div className="flex flex-col md:flex-row gap-4 md:items-center">
                <div className="w-full md:w-48 h-28 border border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
                  {logoPreview || currentLogoUrl ? (
                    <img
                      src={logoPreview || currentLogoUrl || ""}
                      alt="Logo da empresa"
                      className="max-h-24 max-w-full object-contain"
                    />
                  ) : (
                    <p className="text-xs text-gray-500 text-center px-3">Sem logo cadastrada</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {isEditing ? (
                    <>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleRemoveLogo}
                          disabled={isUploadingLogo}
                        >
                          Remover logo
                        </Button>
                        {isUploadingLogo && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Enviando...
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Formatos aceitos: PNG, JPG ou SVG. Tamanho máximo: 5MB.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Faça upload da sua logo para aparecer nos PDFs de orçamento.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informações da Conta */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Informações da Conta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data de Criação</Label>
                <p className="text-sm font-medium">
                  {empresa?.created_at 
                    ? new Date(empresa.created_at).toLocaleDateString('pt-BR')
                    : "Não informado"
                  }
                </p>
              </div>

              <div className="space-y-2">
                <Label>Última Atualização</Label>
                <p className="text-sm font-medium">
                  {empresa?.updated_at 
                    ? new Date(empresa.updated_at).toLocaleDateString('pt-BR')
                    : "Não informado"
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <Card>
          <CardHeader>
            <CardTitle>Ações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate("/assinatura")}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Gerenciar Assinatura
              </Button>
              
              <Separator />
              
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={() => setDialogCancelarContaOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cancelar Conta
              </Button>

              <Separator />
              
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair da Conta
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dialog de Cancelamento de Conta */}
        <Dialog open={dialogCancelarContaOpen} onOpenChange={setDialogCancelarContaOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Cancelar Conta
              </DialogTitle>
              <DialogDescription>
                Esta ação não pode ser desfeita. Todos os seus dados serão permanentemente deletados.
              </DialogDescription>
            </DialogHeader>
            
            <Alert variant="destructive" className="my-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Atenção:</strong> Ao cancelar sua conta:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Todos os seus dados serão deletados permanentemente</li>
                  <li>Sua assinatura será cancelada</li>
                  <li>Você perderá acesso a todos os seus pedidos e dados</li>
                  <li>Esta ação é irreversível</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div>
                <Label htmlFor="confirmacao">
                  Digite <strong>"cancelar"</strong> para confirmar:
                </Label>
                <Input
                  id="confirmacao"
                  value={confirmacaoCancelamento}
                  onChange={(e) => setConfirmacaoCancelamento(e.target.value)}
                  placeholder="cancelar"
                  className="mt-2"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogCancelarContaOpen(false);
                  setConfirmacaoCancelamento("");
                }}
                disabled={cancelandoConta}
              >
                Voltar
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelarConta}
                disabled={cancelandoConta || confirmacaoCancelamento.toLowerCase() !== "cancelar"}
              >
                {cancelandoConta ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  "Confirmar Cancelamento"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}


